// state.js — Mutable runtime state objects and arrays.
// Extracted from script.js (lines 679–718, 1128–1130, 1254–1289, 1602–1630, 3240–3246).
// No imports — all declarations are self-contained.
//
// IMPORTANT — what lives here vs script.js:
//   ✅ Included: const objects/arrays mutated in-place, and let objects that are
//      only mutated via property/method access (never directly reassigned).
//   ❌ Deferred to Stage 2: let primitives that are directly reassigned throughout
//      the code (currentStep, nextJobId, etc.) — these need the AppState pattern.
//      See CLAUDE.md for the full list.

// ── Architecture state ────────────────────────────────────────────────────────
// Deployment architecture settings. Properties are set directly on this object;
// it is never reassigned — safe to export as a live reference.
export const architectureState = {
    veeamVersion: 'v13',
    osPlatform: 'linux',
    deploymentType: 'virtual',
    vbrHa: 'no',
    isDistributed: false,
    databaseType: 'postgresql',
    remoteConfigurationDatabase: 'yes',
    networkConstraints: 'no',
    vbem: 'yes',
    remoteDb: 'no',
    sqlAoag: 'no',
    advancedRbac: 'no',
    siteTopology: '',
    privateNetworking: 'no'
};

// ── Upgrade scope state ───────────────────────────────────────────────────────
// Per-VBR HC/upgrade toggles plus engagement-level RBAC and VBEM flags.
// ⚠️  resetUpgradeScope() in script.js previously used direct reassignment
//     (`upgradeScope = { ... }`). That line must be changed to:
//       Object.assign(upgradeScope, { perVbr: {}, advancedRbac: 'no', vbem: 'yes' });
//     before this module is activated.
export let upgradeScope = {
    perVbr: {},           // { [instanceId]: { hc: false, linuxVsa: true, vsaHa: false } }
    advancedRbac: 'no',
    vbem: 'yes',
};

// Tracks which component-group rows are currently expanded to show per-instance sub-rows.
// Mutated in-place via .add() / .delete() — safe to export.
export let expandedComponentRows = new Set();

// ── Core data arrays ──────────────────────────────────────────────────────────
// All site objects. Mutated in-place via push / splice — never reassigned.
export const sites = [];

// Step 2 hypervisor migration scopes. Mutated in-place — never reassigned.
export const hypervisorMigrationScopes = [];

// Step 2 VB365 repository migration scopes. Mutated in-place — never reassigned.
export const vb365RepoMigrationScopes = [];

// Step 2 VBR repository migration to Veeam Hardened Repository scopes. Mutated in-place — never reassigned.
export const vbrRepoMigrationScopes = [];

// ── Picker UI state ───────────────────────────────────────────────────────────
// Open key + search query state for the workload picker drawer.
export const workloadPickerUiState = {
    openKey: '',
    searchQueryByKey: {},
    expandedLabelsByKey: {}
};

// Open key + search query state for the site scope picker.
export const siteScopePickerUiState = {
    openKey: '',
    searchQueryByKey: {}
};

// ── Step 2 collapse state ─────────────────────────────────────────────────────
// Frozen default — used as a reset source by resetStep2CollapseState() in helpers.js.
export const defaultStep2CollapseState = Object.freeze({
    architecture: true,
    migration: true,
    'hypervisor-migration': true,
    'repo-migration': true,
    protection: true,
    operations: true,
    'rea-advisory': true,
    'post-deploy-care': true
});

// ── Existing environment field schema ─────────────────────────────────────────
// Source-of-truth schema for all existing-environment component fields.
// All derived consts below are computed once from this array.
export const existingEnvironmentFieldSchema = [
    { key: 'veeamOneServers', label: 'VONE', kind: 'count' },
    { key: 'vbemServers', label: 'VBEM', kind: 'count' },
    { key: 'vbrServers', label: 'VBR', kind: 'count' },
    { key: 'proxyServers', label: 'VBR Backup Proxies', kind: 'count', drawerOnly: true },
    { key: 'proxyServersWindows', label: 'VBR Windows Proxies', kind: 'count' },
    { key: 'proxyServersLinux', label: 'VBR Linux Proxies', kind: 'count' },
    { key: 'repositoryServers', label: 'VBR Backup Repositories', kind: 'count' },
    { key: 'wanAccelerators', label: 'WAN accelerators', kind: 'count' },
    { key: 'vb365ProxyServers', label: 'VB365 Proxies', kind: 'count' },
    { key: 'vb365RepositoryServers', label: 'VB365 Repositories', kind: 'count' },
    { key: 'vbfCloudRepositoryServers', label: 'VBfCloud Repositories', kind: 'count' },
    { key: 'veeamAgents', label: 'Veeam Agents', kind: 'count' },
    { key: 'vroServers', label: 'VRO', kind: 'count' },
    { key: 'enterprisePlugins', label: 'Enterprise Plug-Ins', kind: 'count' },
    { key: 'backupJobs', label: 'Backup Jobs', kind: 'count' },
    { key: 'backupCopyJobs', label: 'Backup Copy Jobs', kind: 'count' },
    { key: 'sureBackupJobs', label: 'SureBackup Jobs', kind: 'count' },
    { key: 'cdpPolicies', label: 'CDP Policies', kind: 'count' },
    { key: 'replicationJobs', label: 'Replication Jobs', kind: 'count' },
    { key: 'virtualLab', label: 'Virtual Lab', kind: 'count' }
];

export const existingEnvironmentFieldSchemaByKey = new Map(
    existingEnvironmentFieldSchema.map((field) => [field.key, field])
);

// NOTE: No field in the current schema has `engagementTypes`, so the filter below
// is equivalent to filtering by kind:'count' only. The property is retained so
// engagement-scoped count fields can be added without changing call sites.
export const existingEnvironmentCountFieldOrder = existingEnvironmentFieldSchema
    .filter((field) => field.kind === 'count' && !field.engagementTypes && !field.drawerOnly)
    .map((field) => field.key);

export const existingEnvironmentFieldLabels = Object.fromEntries(
    existingEnvironmentFieldSchema.map((field) => [field.key, field.label])
);

// ── AppState — mutable primitive counters and simple flags ────────────────────
// Formerly `let` declarations in script.js. Exported as a single const object
// so both script.js and helpers.js can import the same reference and mutate
// properties directly (ES module live-binding constraint means individual
// `export let` values can only be written by the declaring module).
export const AppState = {
    // ID counters — incremented by factory functions in helpers.js, reset by resetSites()
    nextSiteId:                     1,
    nextJobId:                      1,   // legacy — retained for state import compatibility; unused in UI
    nextWorkloadRowId:              1,   // legacy — retained for state import compatibility; unused in UI
    nextUnprotectedWorkloadId:      1,   // legacy — retained for state import compatibility; unused in UI
    nextVeeamServerId:              1,
    nextSourceWorkloadId:           1,   // source workload chips (replaces nextJobId + nextUnprotectedWorkloadId)
    nextRepositoryId:               1,   // Step 1 repository entries within a site
    nextHypervisorMigrationId:      1,
    nextVb365RepoMigrationId:       1,
    nextVbrRepoMigrationId:         1,

    // Simple boolean flags
    devModeEnabled: false,

    // Step navigation
    currentStep:           1,
    pendingStepNavigation: null,

    // Feature flags
    hvmEnabled: true,

    // Step 2 section collapse state — mutated in-place by helpers.js
    step2CollapseState: { ...defaultStep2CollapseState },
};

// ── Default toggle/protection states ─────────────────────────────────────────
// Moved from script.js so state-io.js (sanitizeImportedState) can import them
// without pulling in the full script.js dependency chain.

export const defaultToggleState = {
    upgradeHealthCheckReviewRequired: 'no',
    veeamOneRequired: 'no',
    backupCopyRequired: 'yes',
    replicationRequired: 'no',
    tapeIntegrationRequired: 'no',
    cdpReplicationRequired: 'no',
    siemForwardingRequired: 'no',
    enablementRequired: 'no',
    uatRequired: 'no',
    asBuiltRequired: 'yes',
    drRunbookRequired: 'no'
};

export const defaultProtectionState = {
    sureBackupRequired: 'no',
    sureReplicaRequired: 'no'
};
